/**
 * OrderByAddPopover — column-chooser popover for the ORDER BY `[+ add sort]`
 * affordance. Mirrors `FromAddTable`/`SelectListAddColumn`: only the
 * `*Enabled` sub-component subscribes to TanStack Query so VQE-RO unit tests
 * don't need a `QueryClientProvider`.
 *
 * Selecting a suggestion appends a new ORDER BY entry with default direction
 * `ASC`; the user toggles to DESC / NULLS via the rendered item's badges.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove ORDER BY.
 */

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addOrderBy } from '../../edit/edits/addOrderBy';
import { removeOrderBy } from '../../edit/edits/removeOrderBy';
import { formatColumnRef } from '../../edit/edits/selectItemHelpers';
import { runSequentialStatementEdits } from '../../edit/runSequentialStatementEdits';
import { vqeCopy } from '../../ux/vqeCopy';
import { CommandPickerPopover } from '../common/CommandPickerPopover';
import { useInScopeColumnPicker } from '../common/useInScopeColumnPicker';

export interface OrderByAddPopoverProps {
  readonly editContext?: VqeEditContext;
  /** Trigger text; defaults to the clause card's own. The clause add row passes a short one. */
  readonly triggerLabel?: string;
  readonly itemCount: number;
}

export function OrderByAddPopover({ editContext, itemCount, triggerLabel = vqeCopy.actions.addSort }: OrderByAddPopoverProps) {
  if (!editContext) {
    return (
      <CommandPickerPopover triggerLabel={triggerLabel} testId="vqe-order-by-add" />
    );
  }
  return <OrderByAddPopoverEnabled editContext={editContext} itemCount={itemCount} triggerLabel={triggerLabel} />;
}

interface EnabledProps {
  readonly editContext: VqeEditContext;
  readonly triggerLabel: string;
  readonly itemCount: number;
}

function OrderByAddPopoverEnabled({ editContext, itemCount, triggerLabel }: EnabledProps) {
  const { track } = useAnalytics();
  const picker = useInScopeColumnPicker(
    editContext,
    `${editContext.documentId}::${editContext.statementId}::orderby`,
    { selection: 'order-by' },
  );

  const addOne = (key: string) => {
    // A checked row is already a sort key — picking it removes it.
    const selectedIndex = picker.selectedIndexOf(key);
    if (selectedIndex !== undefined) {
      void removeOrderBy(selectedIndex, toCommitCtx(editContext));
      track('feature:vqe_picker_uncheck', { target: 'order_by' });
      return;
    }
    const col = picker.getColumn(key);
    if (!col) return;
    void addOrderBy(formatColumnRef(col, picker.refs), toCommitCtx(editContext), {
      direction: 'asc',
      ...(itemCount > 0 ? { afterIndex: itemCount - 1 } : {}),
    });
  };

  // Bulk "+ all" / "+ N remaining": append the table's columns that aren't
  // sort keys yet as ASC. Each addOrderBy appends at the tail (no afterIndex);
  // sequential awaits let the model adapter splice against the
  // post-previous-commit source.
  const addAll = (relationFolded: string) => {
    const cols = picker.getUnselectedColumnsForRelation(relationFolded);
    if (cols.length === 0) return;
    void runSequentialStatementEdits(editContext, cols, (c, ctx) =>
      addOrderBy(formatColumnRef(c, picker.refs), ctx, { direction: 'asc' }),
    );
    track('feature:vqe_add_all_columns', { count: cols.length, target: 'order_by' });
  };

  return (
    <CommandPickerPopover
      triggerLabel={triggerLabel}
      testId="vqe-order-by-add"
      disabled={!picker.enabled}
      tooltip={picker.enabled ? 'Add a column to ORDER BY' : undefined}
      searchPlaceholder="Search columns…"
      groups={picker.groups}
      onSelect={picker.enabled ? addOne : undefined}
      closeOnSelect
      onGroupAction={picker.enabled ? addAll : undefined}
      scopeToggle={picker.scopeToggle}
      loading={picker.isLoading}
      notice={picker.notice}
      emptyMessage={
        picker.emptyMessage ??
        (picker.hasSnapshot
          ? 'No columns in scope. Refresh the schema or add a FROM table.'
          : 'No schema cache yet — connect to a database to populate suggestions.')
      }
    />
  );
}
